import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { Check } from "lucide-react";
import { createPortal } from "react-dom";
import { ConfirmationButton, ConfirmationInput } from "./primitives";


/** 候选挂到页面浮层，滚动表格不会裁掉列表，也不挤动固定录入区。 */
function EntryCandidatePopup({ open, label, anchor, onClose, children }: { open: boolean; label: string; anchor: RefObject<HTMLDivElement | null>; onClose: () => void; children: ReactNode }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [position, setPosition] = useState<CSSProperties>({ top: 0, left: 0, width: 280 });
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const bounds = anchor.current?.querySelector("input")?.getBoundingClientRect();
      if (!bounds) return;
      const width = Math.min(Math.max(bounds.width, 280), window.innerWidth - 24);
      const height = Math.min(panelRef.current?.scrollHeight || 240, 240);
      const above = window.innerHeight - bounds.bottom - 17 < height && bounds.top > window.innerHeight - bounds.bottom;
      setPosition({ ...(above ? { bottom: window.innerHeight - bounds.top + 5 } : { top: bounds.bottom + 5 }), maxHeight: Math.max(40, Math.min(240, above ? bounds.top - 17 : window.innerHeight - bounds.bottom - 17)), left: Math.max(12, Math.min(bounds.left, window.innerWidth - width - 12)), width });
    };
    const outside = (event: PointerEvent) => { if (!anchor.current?.contains(event.target as Node) && !panelRef.current?.contains(event.target as Node)) closeRef.current(); };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.stopPropagation(); event.preventDefault(); anchor.current?.querySelector("input")?.focus(); closeRef.current(); }
      if (event.key === "ArrowDown" && anchor.current?.contains(document.activeElement)) { event.preventDefault(); panelRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus(); }
    };
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    if (panelRef.current) observer?.observe(panelRef.current);
    place(); window.addEventListener("resize", place); window.addEventListener("scroll", place, true); document.addEventListener("pointerdown", outside); window.addEventListener("keydown", key, true);
    return () => { observer?.disconnect(); window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); document.removeEventListener("pointerdown", outside); window.removeEventListener("keydown", key, true); };
  }, [open, anchor]);
  return open ? createPortal(<div ref={panelRef} style={position} className="confirmation-editor-theme confirmation-entry-options" aria-label={`${label}候选`}>{children}</div>, anchor.current?.closest(".confirmation-sheet") || document.body) : null;
}

/** 对资源搜索作防抖与代际隔离，旧类别、旧主体的响应不能覆盖当前候选。 */
export function EntryAsyncPicker<T>({ label, value, search, contextKey, getKey, getLabel, onChoose, disabled, selectedKey, invalidMessage }: {
  label: string; value?: string | null; search: (query: string) => Promise<T[]>; contextKey: string;
  getKey: (item: T) => string; getLabel: (item: T) => string; onChoose: (item: T) => void; disabled?: boolean; selectedKey?: string; invalidMessage?: string;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const restoreFocus = () => anchorRef.current?.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const searchRef = useRef(search);
  searchRef.current = search;
  useEffect(() => {
    let active = true;
    setResults([]); setError("");
    if (!expanded || disabled) return;
    setLoading(true);
    const timer = setTimeout(() => {
      searchRef.current(query).then((items) => { if (active) setResults(items); })
        .catch(() => { if (active) setError("候选查询失败，请修改关键词后重试"); })
        .finally(() => { if (active) setLoading(false); });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [contextKey, query, expanded, disabled]);
  return <div className="confirmation-entry-picker" ref={anchorRef}>
    <label htmlFor={inputId}>{label}</label><ConfirmationInput id={inputId} aria-label={label} aria-invalid={!!invalidMessage} aria-describedby={invalidMessage ? `${inputId}-issue` : undefined} value={query} placeholder={value || `搜索${label}`} disabled={disabled}
      onFocus={() => setExpanded(true)} onChange={(event) => { setQuery(event.target.value); setExpanded(true); }} />
    {invalidMessage ? <p id={`${inputId}-issue`} className="confirmation-entry-field-hint">{invalidMessage}</p> : null}
    {value ? <span className="confirmation-entry-selected">已选：{value}</span> : null}
    <EntryCandidatePopup open={expanded && !disabled} label={label} anchor={anchorRef} onClose={() => setExpanded(false)}>
      {loading ? <span role="status">查询中…</span> : null}
      {error ? <span role="alert">{error}</span> : null}
      {!loading && !error && !results.length ? <span>暂无匹配候选</span> : null}
      {results.map((item) => <ConfirmationButton key={getKey(item)} size="sm" aria-pressed={selectedKey ? selectedKey === getKey(item) : undefined} onClick={() => { restoreFocus(); onChoose(item); setExpanded(false); setQuery(""); }}><span>{getLabel(item)}</span>{selectedKey === getKey(item) ? <Check aria-hidden /> : null}</ConfirmationButton>)}
      <ConfirmationButton size="sm" variant="ghost" onClick={() => { restoreFocus(); setExpanded(false); }}>收起候选</ConfirmationButton>
    </EntryCandidatePopup>
  </div>;
}

/** 使用已有运行时的供应商搜索结果，不创建供应商或模拟身份。 */
export function EntrySupplierPicker<T extends { id: string; name: string }>({ label, value, options, onSearch, onChoose, loading, error, disabled, selectedKey, invalidMessage }: {
  label: string; value?: string | null; options: T[]; onSearch: (value: string) => void; onChoose: (value: T | null) => void;
  loading?: boolean; error?: unknown; disabled?: boolean; selectedKey?: string; invalidMessage?: string;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const restoring = useRef(false);
  const restoreFocus = () => {
    restoring.current = true;
    anchorRef.current?.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
    restoring.current = false;
  };
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const openSearch = () => {
    if (restoring.current || expanded) return;
    setQuery(""); onSearch(""); setExpanded(true);
  };
  useEffect(() => { if (expanded) { const timer = setTimeout(() => onSearch(query), 250); return () => clearTimeout(timer); } }, [query, expanded, onSearch]);
  return <div className="confirmation-entry-picker" ref={anchorRef}><label htmlFor={inputId}>{label}</label><ConfirmationInput id={inputId} aria-label={label} aria-expanded={expanded} aria-invalid={!!invalidMessage} aria-describedby={invalidMessage ? `${inputId}-issue` : undefined} placeholder={`搜索${label}`} value={expanded ? query : value || ""} readOnly={!expanded} disabled={disabled} style={!expanded ? { caretColor: "transparent", cursor: "pointer" } : undefined}
    onFocus={openSearch} onClick={openSearch} onKeyDown={event => { if (event.key === "Enter" || event.key === "ArrowDown") { event.preventDefault(); openSearch(); } }} onChange={(event) => { setQuery(event.target.value); setExpanded(true); }} />
    {invalidMessage ? <p id={`${inputId}-issue`} className="confirmation-entry-field-hint">{invalidMessage}</p> : null}
    {value ? <span className="confirmation-entry-selected">已选：{value}</span> : null}
    <EntryCandidatePopup open={expanded && !disabled} label={label} anchor={anchorRef} onClose={() => setExpanded(false)}>
      {loading ? <span role="status">查询中…</span> : null}{error ? <span role="alert">候选加载失败，请修改关键词重试</span> : null}
      {!loading && !error && !options.length ? <span>暂无可用主体</span> : null}
      {options.map((item) => <ConfirmationButton key={item.id} size="sm" aria-pressed={selectedKey ? selectedKey === item.id : undefined} onClick={() => { restoreFocus(); onChoose(item); setExpanded(false); setQuery(""); }}><span>{item.name}</span>{selectedKey === item.id ? <Check aria-hidden /> : null}</ConfirmationButton>)}
      <ConfirmationButton size="sm" variant="ghost" onClick={() => { restoreFocus(); onChoose(null); setExpanded(false); }}>待设置</ConfirmationButton>
      <ConfirmationButton size="sm" variant="ghost" onClick={() => { restoreFocus(); setExpanded(false); }}>收起候选</ConfirmationButton>
    </EntryCandidatePopup>
  </div>;
}
